import {useEffect, useState} from 'react'
import {Card, Form} from 'react-bootstrap'
import Select from 'react-select'
import {PhoneInput} from '../../../components/PhoneInput'
import {useMutation} from '@apollo/client'
import {defineMessages, useIntl, type MessageDescriptor} from 'react-intl'
import {toast} from 'react-toastify'
import {UPDATE_AGENT_ONBOARDING_APPLICATION} from '../../../gql/mutations/agentOnboardingMutations'
import {IAgentOnboardingApplication} from '../../../types/agentOnboarding'
import {US_STATES} from '../../../helpers/catalogs'
import {formatPhoneDisplay} from '../../../../../utils/phoneUtils'
import {
  AddressComponent,
  GooglePlacesAutocompleteInput,
  PlaceSelection,
} from '../../../components/GooglePlacesAutocompleteInput'

const M = defineMessages({
  personal: {id: 'onboarding.detail.personal', defaultMessage: 'Personal information'},
  licensing: {id: 'onboarding.detail.licensing', defaultMessage: 'Insurance license'},
  workAuth: {id: 'onboarding.detail.workAuth', defaultMessage: 'Work authorization'},
  experience: {id: 'onboarding.detail.experience', defaultMessage: 'Experience'},
  edit: {id: 'onboarding.info.edit', defaultMessage: 'Edit'},
  save: {id: 'onboarding.info.save', defaultMessage: 'Save changes'},
  cancel: {id: 'onboarding.info.cancel', defaultMessage: 'Cancel'},
  saved: {id: 'onboarding.info.saved', defaultMessage: 'Application updated'},
  failed: {id: 'onboarding.info.failed', defaultMessage: 'Could not update the application'},
  firstName: {id: 'onboarding.info.firstName', defaultMessage: 'First name'},
  lastName: {id: 'onboarding.info.lastName', defaultMessage: 'Last name'},
  email: {id: 'onboarding.detail.field.email', defaultMessage: 'Email'},
  phone: {id: 'onboarding.detail.field.phone', defaultMessage: 'Phone'},
  dob: {id: 'onboarding.detail.field.dob', defaultMessage: 'Date of birth'},
  address1: {id: 'onboarding.info.address1', defaultMessage: 'Address'},
  city: {id: 'onboarding.info.city', defaultMessage: 'City'},
  zip: {id: 'onboarding.info.zip', defaultMessage: 'ZIP'},
  state: {id: 'onboarding.detail.field.state', defaultMessage: 'State'},
  organization: {id: 'onboarding.detail.field.organization', defaultMessage: 'Organization'},
  licenseStatus: {id: 'onboarding.detail.field.status', defaultMessage: 'Status'},
  licenseNumber: {id: 'onboarding.detail.field.licenseNumber', defaultMessage: 'License number'},
  npn: {id: 'onboarding.detail.field.npn', defaultMessage: 'NPN'},
  context: {id: 'onboarding.detail.field.context', defaultMessage: 'Works as'},
  years: {id: 'onboarding.detail.field.years', defaultMessage: 'Years of experience'},
  agency: {id: 'onboarding.detail.field.agency', defaultMessage: 'Previous agency'},
  companies: {id: 'onboarding.detail.field.companies', defaultMessage: 'Contracted companies'},
  notSet: {id: 'onboarding.info.notSet', defaultMessage: 'Not set'},
  invalidEmail: {id: 'onboarding.info.invalidEmail', defaultMessage: 'Enter a valid email address'},
  addressPlaceholder: {
    id: 'onboarding.info.addressPlaceholder',
    defaultMessage: 'Start typing an address…',
  },
  licenseYes: {id: 'onboarding.info.license.yes', defaultMessage: 'Licensed'},
  licenseInProcess: {id: 'onboarding.info.license.inProcess', defaultMessage: 'In process'},
  licenseNo: {id: 'onboarding.info.license.no', defaultMessage: 'Not licensed'},
  authUsCitizen: {id: 'onboarding.info.auth.usCitizen', defaultMessage: 'U.S. citizen'},
  authPermanentResident: {
    id: 'onboarding.info.auth.permanentResident',
    defaultMessage: 'Permanent resident',
  },
  authWorkPermit: {id: 'onboarding.info.auth.workPermit', defaultMessage: 'Work permit'},
  ctxIndividual: {id: 'onboarding.info.ctx.individual', defaultMessage: 'Individual agent'},
  ctxAgency: {id: 'onboarding.info.ctx.agency', defaultMessage: 'Agency / business entity'},
})

const dash = '—'

// Mismos helpers que usan PolicyHolderTab y AddressInfoTab para descomponer la respuesta de
// Google. Están duplicados en esos dos archivos; extraerlos a un módulo común obligaría a tocar
// pantallas críticas, así que aquí se repiten con la deuda anotada.
const getPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
  if (!components) return ''
  return components.find((item) => types.every((type) => item.types.includes(type)))?.long_name ?? ''
}

const getShortPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
  if (!components) return ''
  return (
    components.find((item) => types.every((type) => item.types.includes(type)))?.short_name ?? ''
  )
}

const buildStreetAddress = (components: AddressComponent[] | undefined, fallback?: string) => {
  if (!components || components.length === 0) return fallback ?? ''
  const streetAddress = [getShortPart(components, 'street_number'), getPart(components, 'route')]
    .filter(Boolean)
    .join(' ')
    .trim()
  return streetAddress || fallback || ''
}

type FormState = {
  firstName: string
  lastName: string
  dateOfBirth: string
  email: string
  phone: string
  address1: string
  city: string
  usState: string
  zip: string
  licenseStatus: string
  licenseNumber: string
  npn: string
  workAuthorizationStatus: string
  professionalContext: string
  yearsOfExperience: string
  previousAgency: string
  contractedCompanies: string
}

const toForm = (app: IAgentOnboardingApplication): FormState => ({
  firstName: (app.fullName ?? '').split(' ')[0] ?? '',
  lastName: (app.fullName ?? '').split(' ').slice(1).join(' '),
  dateOfBirth: app.dateOfBirth ?? '',
  email: app.email ?? '',
  phone: app.phone ?? '',
  address1: app.address1 ?? '',
  city: app.city ?? '',
  usState: app.usState ?? '',
  zip: app.zip ?? '',
  licenseStatus: app.licenseStatus ?? '',
  licenseNumber: app.licenseNumber ?? '',
  npn: app.npn ?? '',
  workAuthorizationStatus: app.workAuthorizationStatus ?? '',
  professionalContext: app.professionalContext ?? '',
  yearsOfExperience: app.yearsOfExperience ?? '',
  previousAgency: app.previousAgency ?? '',
  contractedCompanies: app.contractedCompanies ?? '',
})

/**
 * Estos cuatro viven en el módulo, NO dentro de ApplicationInfo.
 *
 * Definirlos dentro creaba un tipo de componente nuevo en cada render, así que React desmontaba
 * y volvía a montar el <input> con cada tecla: el campo perdía el foco y solo aceptaba un
 * carácter por clic. Al ser estables, el nodo del DOM se conserva entre renders.
 */
function Row({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div className='d-flex justify-content-between align-items-center gap-4 py-2 border-bottom border-gray-200'>
      <span className='text-gray-500 fs-7'>{label}</span>
      <span className='text-gray-900 fw-semibold fs-7 text-end'>{children}</span>
    </div>
  )
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <div className='col-xl-6'>
      <Card className='h-100'>
        <Card.Header className='border-0 pt-6'>
          <h3 className='fw-bold text-gray-900'>{title}</h3>
        </Card.Header>
        <Card.Body className='pt-2'>{children}</Card.Body>
      </Card>
    </div>
  )
}

/** Ancho mínimo para que los controles no se aplasten en la celda derecha de la fila. */
const controlWidth = {minWidth: 230}

function Text({
  editing,
  value,
  onChange,
  type = 'text',
  error,
}: {
  editing: boolean
  value: string
  onChange: (value: string) => void
  type?: string
  error?: string
}) {
  if (!editing) return <>{value || dash}</>
  return (
    <div style={controlWidth}>
      <Form.Control
        type={type}
        size='sm'
        value={value}
        isInvalid={!!error}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <small className='text-danger'>{error}</small>}
    </div>
  )
}

function Choice({
  editing,
  value,
  onChange,
  labels,
  notSetLabel,
  labelFor,
}: {
  editing: boolean
  value: string
  onChange: (value: string) => void
  labels: Record<string, MessageDescriptor>
  notSetLabel: string
  labelFor: (value: string, labels: Record<string, MessageDescriptor>) => string
}) {
  if (!editing) return <>{labelFor(value, labels)}</>
  const options = Object.keys(labels).map((key) => ({value: key, label: labelFor(key, labels)}))
  return (
    <div style={controlWidth} className='text-start'>
      <Select
        value={options.find((o) => o.value === value) ?? null}
        options={options}
        placeholder={notSetLabel}
        isClearable
        menuPortalTarget={document.body}
        styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
        onChange={(opt) => onChange((opt as {value: string} | null)?.value ?? '')}
      />
    </div>
  )
}

interface Props {
  app: IAgentOnboardingApplication
  canEdit: boolean
  onSaved: () => void
}

export function ApplicationInfo({app, canEdit, onSaved}: Props) {
  const intl = useIntl()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FormState>(toForm(app))
  const [update, {loading}] = useMutation(UPDATE_AGENT_ONBOARDING_APPLICATION)

  // A manually created application arrives near-empty; adopt whatever the server holds each time
  // it reloads, but never while the reviewer is mid-edit.
  useEffect(() => {
    if (!editing) setForm(toForm(app))
  }, [app, editing])

  const set = (field: keyof FormState, value: string) =>
    setForm((f) => ({...f, [field]: value}))

  const labelOf = (value: string, table: Record<string, MessageDescriptor>) =>
    value && table[value] ? intl.formatMessage(table[value]) : dash

  const licenseLabels = {
    yes: M.licenseYes,
    inProcess: M.licenseInProcess,
    no: M.licenseNo,
  }
  const authLabels = {
    usCitizen: M.authUsCitizen,
    permanentResident: M.authPermanentResident,
    workPermit: M.authWorkPermit,
  }
  const contextLabels = {individual: M.ctxIndividual, agency: M.ctxAgency}

  /** Al elegir una sugerencia, ciudad / estado / ZIP se completan solos; el estado sigue
      editable en su combo por si Google devuelve algo que no aplica. */
  const handleAddressSelect = ({address, addressComponents}: PlaceSelection) => {
    const street = buildStreetAddress(addressComponents, address)
    setForm((f) => {
      const next = {...f, address1: street || f.address1}
      if (!addressComponents) return next
      const city =
        getPart(addressComponents, 'locality') ||
        getPart(addressComponents, 'postal_town') ||
        getPart(addressComponents, 'administrative_area_level_2')
      const stateShort = getShortPart(addressComponents, 'administrative_area_level_1')
      const postalCode = getPart(addressComponents, 'postal_code')
      if (city) next.city = city
      if (postalCode) next.zip = postalCode
      // El combo guarda el código de dos letras, que es lo que espera el backend.
      if (stateShort) next.usState = stateShort
      return next
    })
  }

  // Mismo criterio que el resto del CRM: se avisa en el campo y se bloquea el guardado.
  const emailError =
    form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())
      ? intl.formatMessage(M.invalidEmail)
      : undefined

  const handleSave = async () => {
    if (emailError) {
      toast.error(emailError)
      return
    }
    try {
      const res = await update({
        variables: {
          applicationId: app.id,
          data: {
            firstName: form.firstName,
            lastName: form.lastName,
            dateOfBirth: form.dateOfBirth,
            email: form.email,
            phone: form.phone,
            address1: form.address1,
            city: form.city,
            usState: form.usState,
            zip: form.zip,
            licenseStatus: form.licenseStatus,
            licenseNumber: form.licenseNumber,
            npn: form.npn,
            workAuthorizationStatus: form.workAuthorizationStatus,
            professionalContext: form.professionalContext,
            yearsOfExperience: form.yearsOfExperience,
            previousAgency: form.previousAgency,
            contractedCompanies: form.contractedCompanies,
          },
        },
      })
      const payload = res.data?.updateAgentOnboardingApplication
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      toast.success(intl.formatMessage(M.saved))
      setEditing(false)
      onSaved()
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <>
      {canEdit && (
        <div className='d-flex justify-content-end gap-3 mb-4'>
          {editing ? (
            <>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => {
                  setForm(toForm(app))
                  setEditing(false)
                }}
              >
                {intl.formatMessage(M.cancel)}
              </button>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                disabled={loading}
                onClick={handleSave}
              >
                <i className='bi bi-save me-1' />
                {intl.formatMessage(M.save)}
              </button>
            </>
          ) : (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setEditing(true)}
            >
              <i className='bi bi-pencil me-1' />
              {intl.formatMessage(M.edit)}
            </button>
          )}
        </div>
      )}

      <div className='row g-5 mb-5'>
        <Section title={intl.formatMessage(M.personal)}>
          <Row label={intl.formatMessage(M.firstName)}>
            <Text editing={editing} value={form.firstName} onChange={(v) => set('firstName', v)} />
          </Row>
          <Row label={intl.formatMessage(M.lastName)}>
            <Text editing={editing} value={form.lastName} onChange={(v) => set('lastName', v)} />
          </Row>
          <Row label={intl.formatMessage(M.dob)}>
            <Text editing={editing} value={form.dateOfBirth} onChange={(v) => set('dateOfBirth', v)} type='date' />
          </Row>
          <Row label={intl.formatMessage(M.email)}>
            <Text
              editing={editing}
              value={form.email}
              onChange={(v) => set('email', v)}
              type='email'
              error={emailError}
            />
          </Row>
          <Row label={intl.formatMessage(M.phone)}>
            {editing ? (
              <div style={controlWidth} className='text-start'>
                <PhoneInput
                  id='onboardingPhone'
                  name='phone'
                  value={form.phone}
                  onChange={(_field, value) => set('phone', value)}
                />
              </div>
            ) : (
              formatPhoneDisplay(form.phone) || dash
            )}
          </Row>
          <Row label={intl.formatMessage(M.address1)}>
            {editing ? (
              <GooglePlacesAutocompleteInput
                name='address1'
                value={form.address1}
                placeholder={intl.formatMessage(M.addressPlaceholder)}
                onChange={(e) => set('address1', e.target.value)}
                onPlaceSelect={handleAddressSelect}
              />
            ) : (
              form.address1 || dash
            )}
          </Row>
          <Row label={intl.formatMessage(M.city)}>
            <Text editing={editing} value={form.city} onChange={(v) => set('city', v)} />
          </Row>
          <Row label={intl.formatMessage(M.state)}>
            {editing ? (
              <div style={controlWidth} className='text-start'>
                <Select
                  value={US_STATES.find((s) => s.value === form.usState) ?? null}
                  options={US_STATES}
                  placeholder={intl.formatMessage(M.notSet)}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                  onChange={(opt) => set('usState', (opt as {value: string} | null)?.value ?? '')}
                />
              </div>
            ) : (
              form.usState || dash
            )}
          </Row>
          <Row label={intl.formatMessage(M.zip)}>
            <Text editing={editing} value={form.zip} onChange={(v) => set('zip', v)} />
          </Row>
          <Row label={intl.formatMessage(M.organization)}>{app.organizationName || dash}</Row>
        </Section>

        <Section title={intl.formatMessage(M.licensing)}>
          <Row label={intl.formatMessage(M.licenseStatus)}>
            <Choice
              editing={editing}
              value={form.licenseStatus}
              onChange={(v) => set('licenseStatus', v)}
              labels={licenseLabels}
              notSetLabel={intl.formatMessage(M.notSet)}
              labelFor={labelOf}
            />
          </Row>
          <Row label={intl.formatMessage(M.licenseNumber)}>
            <Text editing={editing} value={form.licenseNumber} onChange={(v) => set('licenseNumber', v)} />
          </Row>
          <Row label={intl.formatMessage(M.npn)}>
            <Text editing={editing} value={form.npn} onChange={(v) => set('npn', v)} />
          </Row>
        </Section>

        <Section title={intl.formatMessage(M.workAuth)}>
          <Row label={intl.formatMessage(M.licenseStatus)}>
            <Choice
              editing={editing}
              value={form.workAuthorizationStatus}
              onChange={(v) => set('workAuthorizationStatus', v)}
              labels={authLabels}
              notSetLabel={intl.formatMessage(M.notSet)}
              labelFor={labelOf}
            />
          </Row>
        </Section>

        <Section title={intl.formatMessage(M.experience)}>
          <Row label={intl.formatMessage(M.context)}>
            <Choice
              editing={editing}
              value={form.professionalContext}
              onChange={(v) => set('professionalContext', v)}
              labels={contextLabels}
              notSetLabel={intl.formatMessage(M.notSet)}
              labelFor={labelOf}
            />
          </Row>
          <Row label={intl.formatMessage(M.years)}>
            <Text editing={editing} value={form.yearsOfExperience} onChange={(v) => set('yearsOfExperience', v)} />
          </Row>
          <Row label={intl.formatMessage(M.agency)}>
            <Text editing={editing} value={form.previousAgency} onChange={(v) => set('previousAgency', v)} />
          </Row>
          <Row label={intl.formatMessage(M.companies)}>
            <Text editing={editing} value={form.contractedCompanies} onChange={(v) => set('contractedCompanies', v)} />
          </Row>
        </Section>
      </div>
    </>
  )
}
